import { useEffect, useRef, useState } from 'react';
import { ChatStream, type ChatBubble } from '../components/ChatStream';
import { STRINGS } from '../lib/i18n';
import { detectLang } from '../lib/rtl';
import { apiUrl } from '../lib/api';
import { authHeaders } from '../lib/session';

export function Chat() {
  const [bubbles, setBubbles] = useState<ChatBubble[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [lang] = useState<'ar' | 'en'>('ar');
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const s = STRINGS.chat;
  const isRtl = lang === 'ar';

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [bubbles]);

  // Active-presence heartbeat. Pings the worker every 25s while the chat tab is
  // open so the dashboard "connected now" tile reflects real users (not just
  // dashboard WS subscribers). The DO uses a 90s grace window — short backgrounds,
  // phone-lock-and-resume, and quick tab-switches all stay counted as connected.
  useEffect(() => {
    let cancelled = false;
    async function ping() {
      try {
        await fetch(apiUrl('/api/heartbeat'), {
          method: 'POST',
          headers: authHeaders({ 'content-type': 'application/json' }),
          credentials: 'include',
        });
      } catch { /* ignore */ }
    }
    ping();
    const id = window.setInterval(() => { if (!cancelled) ping(); }, 25_000);
    const onVis = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; window.clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  async function send(question: string) {
    if (!question.trim() || sending) return;
    setSending(true);
    setInput('');

    // Build history from completed bubbles before adding this turn. Cap to
    // the last 12 messages (6 round-trips) to keep token cost bounded.
    const history = bubbles
      .filter((b) => b.status === 'done' && b.text.trim().length > 0)
      .slice(-12)
      .map((b) => ({ role: b.role, content: b.text }));

    const userId = `u-${Date.now()}`;
    const assistantId = `a-${Date.now()}`;
    setBubbles((b) => [
      ...b,
      { id: userId, role: 'user', text: question, status: 'done' },
      { id: assistantId, role: 'assistant', text: '', status: 'streaming', stage: 'classify', question },
    ]);

    try {
      const res = await fetch(apiUrl('/api/ask'), {
        method: 'POST',
        headers: authHeaders({ 'content-type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ question, history }),
      });

      if (!res.ok || !res.body) {
        // 401 = session expired/missing → bounce to onboarding with a flag so
        // the page can show a friendly notice instead of leaving the user
        // stuck on a broken chat.
        if (res.status === 401) {
          window.location.href = '/?session_expired=1';
          return;
        }
        const errMsg = lang === 'ar'
          ? 'حدث خطأ أثناء الإرسال. اضغط "إعادة المحاولة" أدناه.'
          : 'Something went wrong. Tap Retry below.';
        setBubbles((b) => b.map((x) => x.id === assistantId ? { ...x, text: errMsg, status: 'error' } : x));
        setSending(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'delta') {
              setBubbles((b) => b.map((x) => x.id === assistantId ? { ...x, text: x.text + evt.text } : x));
            } else if (evt.type === 'stage') {
              const stage = evt.stage as ChatBubble['stage'];
              setBubbles((b) => b.map((x) => x.id === assistantId ? { ...x, stage } : x));
            } else if (evt.type === 'cache') {
              // The cache layer (answer or classifier) — surface a friendly
              // "found it" phrase only when it's the answer cache. Classifier
              // cache hits aren't user-meaningful.
              if (evt.layer === 'answer') {
                setBubbles((b) => b.map((x) => x.id === assistantId ? { ...x, stage: 'cache' } : x));
              }
            } else if (evt.type === 'retrieved') {
              // Retrieval finished — keep a generic "preparing answer" phrase.
              setBubbles((b) => b.map((x) => x.id === assistantId ? { ...x, stage: 'answer' } : x));
            } else if (evt.type === 'done') {
              setBubbles((b) => b.map((x) => x.id === assistantId ? { ...x, status: 'done', mode: evt.mode } : x));
            } else if (evt.type === 'error') {
              // Don't leak raw English provider errors into Arabic answers.
              // Show a bilingual "couldn't finish" suffix; the Retry button
              // handles the recovery path.
              const partial = lang === 'ar'
                ? '\n\n— تعذّر استكمال الإجابة. اضغط "إعادة المحاولة".'
                : '\n\n— Couldn\'t finish the answer. Tap Retry.';
              setBubbles((b) => b.map((x) => x.id === assistantId ? { ...x, status: 'error', text: x.text + partial } : x));
            }
          } catch { /* ignored */ }
        }
      }
    } catch {
      const errMsg = lang === 'ar'
        ? 'تعذر الاتصال بالخادم. تحقق من الإنترنت ثم اضغط "إعادة المحاولة".'
        : 'Could not reach the server. Check your connection, then tap Retry.';
      setBubbles((b) => b.map((x) => x.id === assistantId ? { ...x, status: 'error', text: errMsg } : x));
    } finally {
      setSending(false);
    }
  }

  // Re-fire a question when the user clicks the Retry button on an error bubble.
  // Drops the failed assistant + user pair (we'll create new ones) and re-asks.
  function retry(failedAssistantId: string) {
    const idx = bubbles.findIndex((b) => b.id === failedAssistantId);
    if (idx < 1) return;
    const userBubble = bubbles[idx - 1];
    if (!userBubble || userBubble.role !== 'user') return;
    const question = userBubble.text;
    // Remove the failed pair, then send fresh
    setBubbles((b) => b.filter((_, i) => i !== idx && i !== idx - 1));
    setTimeout(() => send(question), 50);
  }

  return (
    <div className="min-h-full flex flex-col bg-tatweer-light" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Brand header */}
      <header className="bg-tatweer-navy w-full sticky top-0 z-10 shadow-card">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-4">
            <img src="/brand/tatweer-white.svg" alt="Tatweer" className="h-12 w-auto" />
            <div>
              <div className="text-lg font-bold text-white bidi-plaintext leading-tight">{s.headerTitle[lang]}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-0.5">Tatweer AI</div>
            </div>
          </div>
          <a
            href="/"
            className="text-xs text-white/80 hover:text-white px-4 py-2 rounded-full ring-1 ring-white/20 hover:ring-white/40 transition"
          >
            {s.headerExit[lang]}
          </a>
        </div>
      </header>

      <main className="flex-1 flex flex-col w-full max-w-3xl mx-auto px-4 pt-3 pb-2">
        <div ref={scrollerRef} className="flex-1 overflow-y-auto tatweer-scroll">
          {bubbles.length === 0 && (
            <div className="text-tatweer-body leading-relaxed space-y-4 mt-2">
              <div className="rounded-2xl bg-white ring-1 ring-tatweer-line shadow-bubble p-6">
                <p className="text-tatweer-body leading-relaxed" dir="rtl">{s.welcome.ar}</p>
              </div>
              <div>
                <p className="text-xs text-tatweer-muted uppercase tracking-wider mb-2 px-1">{s.tryQuestion[lang]}</p>
                <ul className="space-y-2">
                  {s.suggestions.map((sug, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => send(sug[lang])}
                        className="w-full text-start bg-white hover:bg-tatweer-light hover:ring-tatweer-navy/30 ring-1 ring-tatweer-line rounded-xl px-4 py-3 text-sm text-tatweer-body transition"
                        dir={detectLang(sug[lang]) === 'ar' ? 'rtl' : 'ltr'}
                      >
                        {sug[lang]}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <ChatStream bubbles={bubbles} onRetry={retry} />
        </div>

        <form
          className="mt-3 flex gap-2 sticky bottom-0 bg-tatweer-light pt-2 pb-3"
          onSubmit={(e) => { e.preventDefault(); send(input); }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={s.placeholder[lang]}
            className="flex-1 rounded-xl bg-white ring-1 ring-tatweer-line px-4 py-3 text-tatweer-body placeholder:text-tatweer-muted focus:outline-none focus:ring-2 focus:ring-tatweer-navy bidi-plaintext shadow-card"
            disabled={sending}
            dir={detectLang(input) === 'ar' ? 'rtl' : 'ltr'}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="rounded-xl bg-tatweer-green hover:bg-tatweer-green-600 text-white font-semibold px-6 py-3 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-card"
          >
            {sending ? '…' : s.send[lang]}
          </button>
        </form>
      </main>
    </div>
  );
}
